import {
  GeozoneType,
  LegacyGeozoneType,
  TLegacyGeozone,
} from "../../../api/types/Geozone";
import { APP_CONFIG } from "../../../constants/constants";
import { ColorRGB } from "../../../types/common";
import {
  TGeozoneMapData,
  TGeozoneMapDataForAPIs,
  TLatLng,
  TMapState,
} from "../../../types/map";
import { getGeozonePoints } from "../../../utils/geozonePoints";
import { getCircleLocs } from "../../../utils/map";

type TGeozonePrepareMapDataForAPIsProps = {
  shapeType: LegacyGeozoneType;
  seedMapData?: TGeozoneMapData;
  centerPosition?: TLatLng;
  polygonSides?: number | string;
  locs?: TLatLng[];
  radius?: number;
};
type TGeozonePrepareMapDataForAPIs = (
  props: TGeozonePrepareMapDataForAPIsProps
) => TGeozoneMapDataForAPIs;

export const geozonePrepareMapDataForAPIs: TGeozonePrepareMapDataForAPIs = ({
  shapeType,
  seedMapData,
  centerPosition,
  polygonSides,
  locs,
  radius,
}) => {
  const Microsoft = (window as any).Microsoft;
  let mapDataForAPIs = { ...seedMapData };
  delete mapDataForAPIs.color;
  delete mapDataForAPIs.editable;
  delete mapDataForAPIs.ready;

  // centerPosition
  if (seedMapData) {
    if (seedMapData.centerPosition) {
      mapDataForAPIs.centerPosition = {
        latitude: seedMapData.centerPosition.latitude,
        longitude: seedMapData.centerPosition.longitude,
      };
    }
    // prepare appropriate mapDataForAPIs based on shapeType
    switch (
      parseInt((shapeType || LegacyGeozoneType.CIRCLE).toString()) // added ` || LegacyGeozoneType.CIRCLE` to handle undefined shapeType
    ) {
      case LegacyGeozoneType.POLYGON:
      case LegacyGeozoneType.ROUTE:
        if (seedMapData.locs) {
          mapDataForAPIs.locs =
            locs ??
            seedMapData.locs.map((loc) => ({
              latitude: loc.latitude,
              longitude: loc.longitude,
            }));
        }
        if (seedMapData.radius) {
          mapDataForAPIs.radius = radius ?? seedMapData.radius;
        }
        break;
      case LegacyGeozoneType.CIRCLE:
        default:
          mapDataForAPIs.radius = radius ?? seedMapData?.radius ?? APP_CONFIG.MAPS.DEFAULTS.RADIUS;
          break;
    }
  } else {
    // prepare fresh mapDataForAPIs
    mapDataForAPIs.centerPosition =
      centerPosition ?? APP_CONFIG.MAPS.DEFAULTS.CENTER;
    switch (parseInt(shapeType.toString())) {
      case LegacyGeozoneType.ROUTE:
        mapDataForAPIs.locs = [];
        break;
      case LegacyGeozoneType.POLYGON:
        mapDataForAPIs.locs =
          locs ??
          getCircleLocs(
            new google.maps.LatLng(
              mapDataForAPIs.centerPosition.latitude,
              mapDataForAPIs.centerPosition.longitude
            ) as google.maps.LatLng,
            APP_CONFIG.MAPS.DEFAULTS.RADIUS,
            parseInt(`${polygonSides}`) ??
              APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
            true
          ).map((loc: any) => ({
            latitude: loc.lat(),
            longitude: loc.lng(),
          }));
        break;
      case LegacyGeozoneType.CIRCLE:
      default:
        mapDataForAPIs.radius = radius ?? APP_CONFIG.MAPS.DEFAULTS.RADIUS;
        break;
    }
  }
  return mapDataForAPIs;
};

type TGeozonePrepareMapStateProps = TGeozonePrepareMapDataForAPIsProps & {
  seedMapState: TMapState;
  color?: ColorRGB;
  editable?: boolean;
  ready?: boolean;
};
type TGeozonePrepareMapState = (
  props: TGeozonePrepareMapStateProps
) => TMapState;

export const geozonePrepareMapState: TGeozonePrepareMapState = ({
  seedMapState,
  shapeType,
  seedMapData,
  centerPosition,
  polygonSides,
  locs,
  radius,
  color,
  editable,
  ready,
}) => {
  const mapCenter = centerPosition ?? seedMapState.mapCenter;
  const mapDataForAPIs = geozonePrepareMapDataForAPIs({
    shapeType,
    seedMapData,
    centerPosition: mapCenter,
    polygonSides,
    locs,
    radius,
  });
  return {
    ...seedMapState,
    mapCenter,
    mapData: {
      ...seedMapData,
      ...mapDataForAPIs,
      radius: radius ?? seedMapData?.radius,
      color: color ?? seedMapData?.color ?? APP_CONFIG.MAPS.DEFAULTS.ZONE_COLOR,
      editable: editable ?? seedMapState?.mapData?.editable,
      ready: ready ?? seedMapState.mapData?.ready ?? false,
    },
    mapDataForAPIs,
  };
};

export const getGeozoneLocs = (geozone: Partial<TLegacyGeozone>) => {
  if (geozone?.zonetype?.toString() === LegacyGeozoneType.CIRCLE.toString()) {
    if (geozone?.latitude1 && geozone?.longitude1) {
      return [
        {
          latitude: geozone.latitude1,
          longitude: geozone.longitude1,
        },
      ];
    }
    return [];
  }
  return getGeozonePoints(geozone as Parameters<typeof getGeozonePoints>[0]);
};
